import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, InputNumber, Input, Form, Space, ColorPicker, notification, Switch } from 'antd';

import IBaseComponentProps from '@components/base-component-props';

import FormHeader from '@controls/form-header/form-header';

import { exception } from '@extensions/notification';
import { NoColor } from '@core/types';

import { userLoaded } from '@store/actions';
import { useAppSelector, useAppDispatch } from '@store/hooks';

import { IUserSession } from '@entities/user-session';
import { ICountry } from '@entities/country';

import { Permission, hasPermission } from '@enums/permission';

const Country = (props: IBaseComponentProps) => {
    const { serverFetch } = props;

    const { id } = useParams();

    const navigate = useNavigate();
    const [api, contextHolder] = notification.useNotification();

    const d = useAppDispatch();
    const userSession = useAppSelector<IUserSession>((s) => s.userSession);

    const [entity, setEntity] = useState<ICountry>({
        id: undefined,
        name: undefined,
        code: undefined,
        sequence: undefined,
        generateTariff: false,
    });
    const [loading, setLoading] = useState<boolean>(false);
    const [isViewOnly] = useState<boolean>(!hasPermission(userSession.permissions, Permission.ManageCountries));

    useEffect(() => {
        if (!id) {
            return;
        }

        setLoading(true);

        serverFetch(`countries/${id}`, { method: 'GET' })
            .then((data: ICountry) => {
                setEntity(data);
                setLoading(false);
            })
            .catch((ex: any) => {
                exception(api, 'Ошибка получения стран', ex, () => d(userLoaded(undefined)));
                setLoading(false);
            });
    }, []);

    const onFinishSave = () => {
        setLoading(true);

        if (entity.color == NoColor) {
            entity.color = undefined;
        }

        serverFetch(`countries`, { method: id ? 'PUT' : 'POST', bodyData: entity })
            .then(() => {
                setLoading(false);
                navigate(-1);
            })
            .catch((ex: any) => {
                setLoading(false);
                exception(api, 'Ошибка сохранения страны', ex, () => d(userLoaded(undefined)));
            });
    };

    return (
        <>
            <FormHeader title={`${id ? (isViewOnly ? 'Информация о стране' : 'Изменить страну') : 'Добавить страну'}`} />
            {!loading && (
                <Form colon={false} labelCol={{ span: 6 }} wrapperCol={{ span: 8 }} onFinish={onFinishSave}>
                    <Form.Item
                        initialValue={entity?.name}
                        required
                        label='Название'
                        name='name'
                        rules={[{ required: true, message: 'Укажите название страны' }]}
                    >
                        <Input
                            disabled={isViewOnly}
                            autoFocus
                            onChange={(data) => {
                                setEntity({ ...entity, name: data.target.value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        wrapperCol={{ span: 3 }}
                        initialValue={entity?.code}
                        required
                        label='Код'
                        name='code'
                        rules={[
                            { required: true, message: 'Укажите код страны' },
                            {
                                validator: async (_, value) => {
                                    if (/^[A-Z]/gi.test(value)) {
                                        return Promise.resolve();
                                    }

                                    return Promise.reject('Только заглавные буквы латинского алфавита');
                                },
                            },
                        ]}
                    >
                        <Input
                            showCount
                            maxLength={3}
                            disabled={isViewOnly}
                            onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toUpperCase())}
                            onChange={(data) => {
                                setEntity({ ...entity, code: data.target.value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        wrapperCol={{ span: 3 }}
                        initialValue={entity?.airportCode}
                        label='Аэропорт'
                        name='airportCode'
                        rules={[
                            {
                                validator: async (_, value) => {
                                    if (/^[A-Z]/gi.test(value)) {
                                        return Promise.resolve();
                                    }

                                    return Promise.reject('Только заглавные буквы латинского алфавита');
                                },
                            },
                        ]}
                    >
                        <Input
                            showCount
                            maxLength={3}
                            disabled={isViewOnly}
                            onInput={(e) => ((e.target as HTMLInputElement).value = (e.target as HTMLInputElement).value.toUpperCase())}
                            onChange={(data) => {
                                setEntity({ ...entity, airportCode: data.target.value });
                            }}
                        />
                    </Form.Item>

                    <Form.Item
                        initialValue={entity?.sequence}
                        label='Порядковый номер'
                        name='sequence'
                        required
                        rules={[{ required: true, message: 'Укажите порядковый номер' }]}
                    >
                        <InputNumber
                            disabled={isViewOnly}
                            onChange={(value) => {
                                setEntity({ ...entity, sequence: value as number });
                            }}
                        />
                    </Form.Item>
                    <Form.Item label='Цвет' name='color' initialValue={entity?.color || NoColor}>
                        <ColorPicker
                            format='hex'
                            styles={{ popupOverlayInner: { width: 292 } }}
                            onChangeComplete={(value) => {
                                setEntity({ ...entity, color: value.toHexString() });
                            }}
                            presets={[
                                {
                                    label: '',
                                    colors: [
                                        NoColor,
                                        '#ffa500',
                                        '#f37735',
                                        '#00aedb',
                                        '#00b159',
                                        '#d11141',
                                        '#a200ff',
                                        '#00aba9',
                                        '#007b7b',
                                        '#f6546a',
                                        '#8b0000',
                                        '#0a75ad',
                                        '#daa520',
                                        '#ff6666',
                                        '#0000ff',
                                        '#000080',
                                        '#ff7373',
                                    ],
                                },
                            ]}
                            panelRender={(_, { components: { Picker, Presets } }) => (
                                <div
                                    className='custom-panel'
                                    style={{
                                        display: 'flex',
                                        width: 250,
                                    }}
                                >
                                    <div
                                        style={{
                                            flex: 1,
                                        }}
                                    >
                                        <Presets />
                                    </div>
                                </div>
                            )}
                        />
                    </Form.Item>
                    <Form.Item
                        label='Генерировать тариф'
                        initialValue={entity?.generateTariff}
                        valuePropName='checked'
                        name='generateTariff'
                    >
                        <Switch
                            disabled={isViewOnly}
                            onChange={(value) => {
                                setEntity({ ...entity, generateTariff: value });
                            }}
                        />
                    </Form.Item>
                    <Form.Item label='' wrapperCol={{ offset: 4, span: 6 }}>
                        <Space size={'small'} style={{ float: 'right' }}>
                            <Button type='text' onClick={() => navigate(-1)}>
                                Отменить
                            </Button>
                            {!isViewOnly && (
                                <Button type='primary' htmlType='submit' loading={loading}>
                                    Сохранить
                                </Button>
                            )}
                        </Space>
                    </Form.Item>
                </Form>
            )}

            {contextHolder}
        </>
    );
};

export default Country;
